import SEO from '../components/SEO';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Cloud, Server, Shield, Zap, TrendingUp, DollarSign, Globe, Clock, CheckCircle2, AlertTriangle, Target, Award, Users, ChevronDown, ChevronUp, RefreshCw, Lock, BarChart3 } from 'lucide-react';
import ProductCTA from '../components/ProductCTA';

const OnPremiseVsCloud = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const cloudBenefits = [
    {
      icon: TrendingUp,
      title: "Better performance and user experience",
      points: [
        "More consistent SAP performance with modern cloud infrastructure",
        "Scalable resources to match demand (growth, peaks, reporting periods)",
        "Reduced 'slow system' blame game caused by ageing servers and storage"
      ]
    },
    {
      icon: Shield,
      title: "Higher resilience and business continuity",
      points: [
        "Improved uptime through professionally managed hosting",
        "Automated backups and monitoring designed for recovery, not hope",
        "Stronger disaster recovery options than most on-prem setups can justify"
      ]
    },
    {
      icon: Lock,
      title: "Stronger security posture",
      points: [
        "Enterprise-grade security controls aligned to cloud best practices",
        "Centralised monitoring and patching to reduce vulnerability windows",
        "Less dependence on one physical site and the risks that come with it"
      ]
    },
    {
      icon: DollarSign,
      title: "Predictable costs and fewer surprises",
      points: [
        "Move from large capital spend (CapEx) to predictable monthly operating cost (OpEx)",
        "No more hardware refresh cycles every 3-5 years",
        "Lower risk of emergency spend when something fails unexpectedly"
      ]
    },
    {
      icon: Zap,
      title: "Faster change, easier growth",
      points: [
        "Quicker provisioning for new users, locations, or environments",
        "Easier to support remote work and multi-site teams",
        "A foundation that supports future integrations and modernisation"
      ]
    }
  ];

  const migrationSteps = [
    { step: "1", title: "Assess & Plan", desc: "Understand your SAP B1 landscape, usage, add-ons, and priorities" },
    { step: "2", title: "Design & Prepare", desc: "Right-size cloud resources and define backup/DR needs" },
    { step: "3", title: "Migrate", desc: "Move your environment with a controlled cutover plan" },
    { step: "4", title: "Optimise", desc: "Tune performance and cost, confirm monitoring and resilience" },
    { step: "5", title: "Run & Improve", desc: "Ongoing managed service, not 'set and forget'" }
  ];

  const faqs = [
    {
      q: "Will SAP Business One feel faster?",
      a: "In most cases, yes - especially where on-prem hardware is constrained or inconsistent. Performance becomes more predictable when the underlying infrastructure is modern and monitored."
    },
    {
      q: "Is cloud more secure than on-prem?",
      a: "Cloud isn't 'automatically secure,' but it enables stronger, more consistent security controls when managed properly - and removes many risks tied to physical servers and local environments."
    },
    {
      q: "Do we have to buy new servers?",
      a: "No. That's the point - cloud removes the refresh cycle and spreads cost predictably."
    },
    {
      q: "Can we support remote users better?",
      a: "Yes. Cloud hosting typically improves accessibility and consistency for distributed teams."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="On-Premise vs Cloud SAP Business One | Migration Benefits & Guide"
        description="Move SAP Business One to the cloud with Ascarii. Better performance, lower costs, global access. Expert migration with zero data loss. Free assessment."
        keywords="SAP B1 cloud migration, on-premise to cloud, SAP cloud benefits, ERP migration, cloud vs on-premise"
        canonical="https://www.ascarii.com/on-premise-vs-cloud"
      />

      {/* Hero */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-blue-700 via-sky-800 to-blue-900">
        <div className="absolute inset-0 z-0">
          <img src="/hero-background.jpeg" alt="Cloud Sky" className="w-full h-full object-cover opacity-40" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">SAP Business One on AWS</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Move SAP Business One from On-Premise to Cloud with Ascarii
              </h1>
              <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
                From server racks to always-on performance - without the hardware headaches.
              </p>
              <p className="text-base sm:text-lg text-blue-200 max-w-3xl mx-auto leading-relaxed">
                Running SAP Business One on ageing on-premise infrastructure holds teams back: unpredictable downtime, slow performance, costly refresh cycles, and constant firefighting. Ascarii helps you move SAP Business One to the cloud on AWS so you get faster, more reliable SAP - with simpler costs and stronger security.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* On-Premise Risks */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why the On-Premise Model is Now a Risk
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              On-premise used to be the default: buy servers, install SAP B1, and hope the infrastructure keeps up. Today, that model struggles.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Hardware ages faster than your business plans", icon: Server },
              { title: "Availability expectations are higher than ever", icon: Clock },
              { title: "Redundancy and disaster recovery are expensive to build properly", icon: Shield },
              { title: "Performance issues become harder to diagnose (and fix)", icon: BarChart3 },
              { title: "Security patching, backups, and monitoring take time you don't have", icon: Lock },
              { title: "Every failure turns into an urgent operational problem", icon: AlertTriangle }
            ].map((risk, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white border-l-4 border-red-600 p-6 rounded-lg shadow-md text-center"
              >
                <risk.icon className="w-8 h-8 text-red-600 mb-3 mx-auto" />
                <p className="text-gray-700 font-medium">{risk.title}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="mt-12 p-8 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl text-white text-center"
          >
            <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">The Bottom Line</h3>
            <p className="text-lg text-red-100">
              On-prem creates cost spikes, operational risk, and limits growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cloud Benefits */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="absolute top-10 right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What You Gain by Moving SAP B1 to the Cloud
            </h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">
              Migrating your SAP Business One environment to AWS via Ascarii isn&apos;t just an infrastructure change - it&apos;s a better operating model for your business.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: "Better Performance", strapline: "Faster, more reliable", desc: "More consistent SAP performance with modern cloud infrastructure. Scalable resources to match demand (growth, peaks, reporting periods). Reduced 'slow system' blame game." },
              { icon: Shield, title: "Higher Resilience", strapline: "Always available", desc: "Improved uptime through professionally managed hosting. Automated backups and monitoring designed for recovery. Stronger disaster recovery than most on-prem setups." },
              { icon: Lock, title: "Stronger Security", strapline: "Protected and compliant", desc: "Enterprise-grade security controls aligned to cloud best practices. Centralised monitoring and patching. Less dependence on one physical site." },
              { icon: DollarSign, title: "Predictable Costs", strapline: "No surprises", desc: "Move from large capital spend (CapEx) to predictable monthly operating cost (OpEx). No more hardware refresh cycles every 3-5 years. Lower risk of emergency spend." },
              { icon: Zap, title: "Faster Growth", strapline: "Scale easily", desc: "Quicker provisioning for new users, locations, or environments. Easier to support remote work and multi-site teams. A foundation for future integrations." },
              { icon: Globe, title: "Global Access", strapline: "Anywhere, anytime", desc: "Access SAP Business One securely from anywhere, on any device. Support distributed teams with consistent performance. Enable remote work seamlessly." }
            ].map((benefit, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.4, delay: idx * 0.05 }} 
                viewport={{ once: true }} 
                className="group flex items-start gap-4 p-6 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all"
              >
                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-400 transition-all">
                  <benefit.icon className="w-7 h-7 text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-sm text-cyan-300 font-medium mb-2">{benefit.strapline}</p>
                  <p className="text-blue-100 text-sm leading-relaxed">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ascarii */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Ascarii
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ascarii takes care of the heavy lifting so you can focus on running your business (not servers).
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Cloud, title: "SAP Business One hosted on AWS", desc: "Managed environment with enterprise infrastructure" },
              { icon: BarChart3, title: "Proactive monitoring", desc: "Operational management and health checks" },
              { icon: RefreshCw, title: "Backup and recovery built-in", desc: "Automated, tested disaster recovery" },
              { icon: Shield, title: "Security best practices", desc: "Consistently applied protection and compliance" },
              { icon: Target, title: "Clear migration path", desc: "From your current on-prem setup to cloud" },
              { icon: Award, title: "Managed service", desc: "Not 'set and forget' - ongoing optimization" }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Process */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              What the Migration Looks Like
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              A clear, proven approach to moving your SAP Business One from on-premise to cloud
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6">
            {migrationSteps.map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center"
              >
                <div className="w-12 h-12 bg-cyan-400 text-blue-900 rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-blue-200">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about moving SAP B1 to the cloud
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 10 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-sky-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }} 
              className="lg:col-span-2"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ready to Move SAP B1 to the Cloud?</h2>
              <p className="text-xl text-gray-600 mb-8">If you want faster SAP performance, fewer infrastructure emergencies, stronger resilience, and predictable monthly costs</p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center">
                    <Cloud className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Expert consultation</h4>
                    <p className="text-gray-600 text-sm">Cloud readiness assessment</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center">
                    <Target className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Tailored solutions</h4>
                    <p className="text-gray-600 text-sm">Migration plan for your business</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center">
                    <Award className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Proven results</h4>
                    <p className="text-gray-600 text-sm">Successful migrations delivered</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }} 
              className="lg:col-span-3"
            >
              <ProductCTA productName="SAP Business One Cloud Migration" brandColor="sky-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnPremiseVsCloud;
